<template>
  <ModalMainModal :size="7" @close="closeForm">
    <template #content>
      <div v-if="activeTab === Tabs.Form" class="text-left sm:px-6">
        <p class="text-dark text-2xl font-bold mb-4">{{ t('formTitle') }}</p>
        <div class="grid grid-rows-2 grid-cols-6 gap-x-5 gap-y-3 mb-4">
          <div v-for="(input, index) in inputs" :key="index" :class="[index !== 6 ? 'sm:col-span-3' : '', input.model === 'subject_detail' && props.subject ? 'hidden' : '']" class="col-span-6">
            <p class="mb-1 font-semibold text-dark">{{ input.name }}</p>
            <div v-if="input.type === InputType.Language" class="relative z-10">
              <button
                :class="openLanguage ? 'rounded-t-lg' : 'rounded-lg'"
                class="bg-light px-4 py-2 w-full text-sm flex items-center justify-between space-x-3 text-darkgray focus:outline-none"
                @click="openLanguage = !openLanguage"
              >
                <div class="flex items-center space-x-3">
                  <img class="w-5 h-5 my-auto" :src="`/icons/flags/flag_${data.language}.svg`" alt="" />
                  <span>{{ getLanguageName(data.language) }}</span>
                </div>
                <svg :class="openLanguage ? 'rotate-180' : ''" class="transition-all" width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 1L6 6L1 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <transition name="dropdown">
                <div v-if="openLanguage" class="absolute left-0 w-full bg-light rounded-b-md">
                  <div v-for="language in availableLocales" :key="language.code">
                    <button class="flex items-center space-x-3 py-2 px-4 w-full hover:bg-lightgray hover:rounded-lg transition-all" @click="setLanguage(language.code)">
                      <img class="w-4 h-4" :src="`/icons/flags/flag_${language.code}.svg`" alt="" />
                      <div class="text-sm text-darkgray">{{ language.name }}</div>
                    </button>
                  </div>
                </div>
              </transition>
            </div>
            <div v-else-if="input.type === InputType.Dropdown" class="relative">
              <button
                :disabled="input.model === 'subject_detail' && data.subject === null"
                :class="[openDropdown ? 'rounded-t-lg' : 'rounded-lg', input.model === 'subject_detail' && data.subject === null ? 'opacity-50 cursor-not-allowed' : '']"
                class="bg-light px-4 py-2 w-full text-sm flex items-center justify-between space-x-3 text-darkgray focus:outline-none"
                @click="openDropdown === index ? (openDropdown = null) : !props.subject ? (openDropdown = index) : null"
              >
                <span>{{ data[input.model] || t('select') }}</span>
                <svg
                  v-if="!props.subject"
                  :class="openDropdown === index ? 'rotate-180' : ''"
                  class="transition-all"
                  width="12"
                  height="7"
                  viewBox="0 0 12 7"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M11 1L6 6L1 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <transition name="dropdown">
                <div v-if="openDropdown === index" class="absolute left-0 w-full bg-light rounded-b-md max-h-32 overflow-y-scroll shadow z-10">
                  <div v-for="option in getInputOptions(input.model)" :key="option">
                    <button class="py-2 px-4 w-full hover:bg-lightgray text-darkgray transition-all text-left" @click=";[(data[input.model] = option), (openDropdown = null)]">
                      <span class="text-sm">{{ option }}</span>
                    </button>
                  </div>
                </div>
              </transition>
            </div>
            <input
              v-else-if="input.type === InputType.Text || input.type === InputType.Email || input.type === InputType.Number"
              v-model="data[input.model]"
              :disabled="(input.model === 'name' && store.miioUser?.name) || (input.model === 'email' && store.miioUser?.email) || (input.model === 'phone_number' && store.miioUser?.phone_number)"
              :type="input.type"
              :placeholder="input.placeholder"
              class="bg-light text-sm py-2 px-4 rounded-xl w-full form-input focus:outline-none focus:ring-0 border-0 text-darkgray"
            />
            <textarea
              v-else-if="input.type === InputType.Textarea"
              v-model="data.message"
              rows="5"
              :placeholder="input.placeholder"
              class="bg-light text-sm py-2 px-4 rounded-xl w-full form-input focus:outline-none focus:ring-0 max-h-28 border-0"
            ></textarea>
          </div>
        </div>
        <div v-if="errorMessage" class="flex items-center bg-danger bg-opacity-10 px-4 py-1.5 rounded-lg text-danger space-x-1 mt-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-5 h-5">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          <p class="text-sm font-semibold">{{ errorMessage }}</p>
        </div>
        <div class="flex justify-between space-x-10">
          <ButtonMain
            :text="t('submit')"
            :loading="loading"
            :disabled="!ready"
            :type="ready ? ButtonType.Primary : ButtonType.Disabled"
            class="mx-auto mt-6 min-w-[8rem]"
            @click="sendContactRequest"
          />
        </div>
      </div>
      <div v-else>
        <div class="flex items-center justify-center">
          <div class="text-center">
            <img src="/icons/green/contact-bot.svg" class="mx-auto mb-4" alt="" />
            <p class="font-semibold text-dark text-2xl mb-2">{{ t('submit-success') }}</p>
            <p class="text-mediumgray mb-3 max-w-xs mx-auto">{{ t('submit-success-description') }}</p>
            <ButtonMain :text="t('close')" :type="ButtonType.Primary" class="mx-auto mt-6 min-w-[8rem]" @click="closeForm" />
          </div>
        </div>
      </div>
    </template>
  </ModalMainModal>
</template>
<script setup lang="ts">
  import { ButtonType } from '~/types/enums'
  import { useStore } from '~/stores/main'

  const { t, locale, locales } = useI18n()
  const store = useStore()

  const props = defineProps({
    subject: {
      type: String,
      required: false,
      default: null
    }
  })

  const emit = defineEmits(['close'])

  enum InputType {
    Text = 'text',
    Email = 'email',
    Number = 'number',
    Textarea = 'textarea',
    Dropdown = 'dropdown',
    Language = 'language'
  }

  enum Tabs {
    Form = 1,
    Success = 2
  }

  type FormInputs = {
    name: string
    model: string
    type: string
    placeholder?: string
    options?: string[] | object
  }

  const activeTab = ref(Tabs.Form)
  const openLanguage = ref(false)
  const openDropdown = ref(null)
  const errorMessage = ref<string | null>(null)
  const loading = ref(false)

  const inputs: FormInputs[] = [
    { name: t('name'), model: 'name', type: InputType.Text, placeholder: t('name_placeholder') },
    { name: t('phone_number'), model: 'phone_number', type: InputType.Text, placeholder: '000 000 000' },
    { name: t('language'), model: 'language', type: InputType.Language },
    { name: 'Email', type: InputType.Email, model: 'email', placeholder: t('email_placeholder') },
    { name: t('contactReason'), model: 'subject', type: InputType.Dropdown, options: [t('purchase'), t('delivery'), t('returns'), t('installation')] },
    {
      name: t('contactReasonDetail'),
      model: 'subject_detail',
      type: InputType.Dropdown,
      options: [
        [t('cPayment'), t('cPrice'), t('cCoupon'), t('cCancel'), t('cPaymentNotAccepted'), t('cVatRefund')],
        [t('cNotReceived'), t('cDelayed'), t('cCancelOrder'), t('cNotHome'), t('cWhere')],
        [t('cDefect'), t('cWrongSpec'), t('cNotExpected')],
        [t('cAppointment'), t('cProblems')]
      ]
    },
    { name: t('message'), model: 'textarea', type: InputType.Textarea, placeholder: t('question') }
  ]

  type FormData = {
    name: string | null
    phone_number: string | null
    email: string | null
    language: string
    subject: string | null
    subject_detail?: string
    message: string | null
    contact_hour: string
  }

  const data = reactive<FormData>({
    name: store.miioUser?.name || null,
    phone_number: store.miioUser?.phone_number || null,
    email: store.miioUser?.email || null,
    language: store.miioUser?.language || locale.value,
    subject: props.subject || null,
    message: null,
    contact_hour: 'ASAP'
  })

  async function sendContactRequest() {
    if (loading.value) return
    loading.value = true
    if (data.subject_detail) {
      data.subject = `${data.subject} - ${data.subject_detail}`
      delete data.subject_detail
    }
    errorMessage.value = null
    const { error } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/phone-call-request`, 'POST', data)
    if (error) {
      errorMessage.value = t('error')
    } else {
      activeTab.value = Tabs.Success
    }
    loading.value = false
  }

  const ready = computed(() => {
    return Object.values(data).every((field) => field !== null && field !== '')
  })

  const availableLocales = computed(() => {
    return locales.value.filter((locale) => locale.code !== data.language)
  })

  function closeForm() {
    emit('close')
  }

  function setLanguage(code: string) {
    data.language = code
    openLanguage.value = false
  }

  function getLanguageName(language: string) {
    return locales.value.find((locale) => locale.code === language).name
  }

  function getInputOptions(model: string) {
    if (model === 'subject') {
      return inputs.find((input) => input.model === model)?.options
    } else {
      return inputs.find((input) => input.model === model)?.options[inputs.find((input) => input.model === 'subject')?.options.indexOf(data.subject)]
    }
  }
</script>
<i18n>
{
  "pt": {
    "formTitle": "Formulário de contacto",
    "name" :"Nome",
    "name_placeholder": "Indique o seu nome",
    "phone_number": "Número de telemóvel",
    "language": "Idioma",
    "email_placeholder": "Indique o seu e-mail",
    "message": "Exponha de forma breve a sua questão",
    "subject": "Assunto",
    "submit": "Submeter",
    "submit-success": "O pedido de contacto foi registado!",
    "submit-success-description": "A equipa de apoio ao cliente miio irá entrar em contacto com a maior brevidade.",
    "error": "Ocorreu um erro ao realizar o pedido.",
    "select": "Selecionar opção",
    "question": "Questão",
    "phoneIndicative": "+351",
    "contactReason": "Motivo do contacto",
    "purchase": "Compra",
    "delivery": "Entrega",
    "returns": "Devolução/trocas",
    "installation": "Instalação",
    "contactReasonDetail": "Detalhe do motivo",
    "cPayment": "Não consigo pagar",
    "cPrice": "O preço do produto estava diferente do anunciado",
    "cCoupon": "Não consigo aplicar um cupão",
    "cCancel": "Quero cancelar a minha encomenda",
    "cPaymentNotAccepted": "Pagamento não foi aceite",
    "cVatRefund": "Reembolso do IVA",
    "cNotReceived": "Não recebi a minha encomenda",
    "cDelayed": "A minha entrega atrasou",
    "cCancelOrder": "Quero cancelar a minha encomenda",
    "cNotHome": "Não estava em casa no momento da entrega, como recolher",
    "cWhere": "Como sei onde está a minha encomenda",
    "cDefect": "O meu produto veio com defeito",
    "cWrongSpec": "Comprei a especificação errada",
    "cNotExpected": "Não é o que esperava e desejo devolver",
    "cAppointment": "Marcação com instaladores",
    "cProblems": "Problemas na instalação"
  },
  "en": {
    "formTitle": "Contact Form",
    "name": "Name",
    "name_placeholder": "Enter your name",
    "phone_number": "Phone number",
    "language": "Language",
    "email_placeholder": "Enter your email",
    "message": "Briefly describe your question",
    "subject": "Subject",
    "subject_placeholder": "Please select a subject",
    "submit": "Submit",
    "submit-success": "The contact request has been registered!",
    "submit-success-description": "The miio customer support team will be in touch as soon as possible.",
    "error": "An error occurred while making the request.",
    "select": "Select option",
    "question": "Question",
    "phoneIndicative": "+44",
    "contactReason": "Contact reason",
    "purchase": "Purchase",
    "delivery": "Delivery",
    "returns": "Returns/Exchanges",
    "installation": "Installation",
    "contactReasonDetail": "Reason Detail",
    "cPayment": "Unable to make payment",
    "cPrice": "Product price was different from advertised",
    "cCoupon": "Unable to apply a coupon",
    "cCancel": "I want to cancel my order",
    "cPaymentNotAccepted": "Payment was not accepted",
    "cVatRefund": "VAT Refund",
    "cNotReceived": "I did not receive my order",
    "cDelayed": "My delivery is delayed",
    "cCancelOrder": "I want to cancel my order",
    "cNotHome": "I was not at home during delivery, how to collect",
    "cWhere": "How do I know where my order is",
    "cDefect": "My product arrived defective",
    "cWrongSpec": "I purchased the wrong specification",
    "cNotExpected": "It's not what I expected, and I want to return it",
    "cAppointment": "Appointment with installers",
    "cProblems": "Installation problems"
  },
  "es": {
    "formTitle": "Formulario de contacto",
    "name": "Nombre",
    "name_placeholder": "Ingrese su nombre",
    "phone_number": "Número de teléfono",
    "language": "Idioma",
    "email_placeholder": "Ingrese su correo electrónico",
    "message": "Describa brevemente su pregunta",
    "subject": "Asunto",
    "subject_placeholder": "Seleccione un asunto",
    "submit": "Enviar",
    "submit-success": "¡Se ha registrado la solicitud de contacto!",
    "submit-success-description": "El equipo de atención al cliente miio se pondrá en contacto a la mayor brevedad.",
    "error": "Se produjo un error al realizar la solicitud.",
    "select": "Seleccionar opción",
    "question": "Pregunta",
    "phoneIndicative": "+34",
    "contactReason": "Motivo de contacto",
    "purchase": "Compra",
    "delivery": "Entrega",
    "returns": "Devoluciones/Cambios",
    "installation": "Instalación",
    "contactReasonDetail": "Detalle del motivo",
    "cPayment": "No puedo realizar el pago",
    "cPrice": "El precio del producto era diferente al anunciado",
    "cCoupon": "No puedo aplicar un cupón",
    "cCancel": "Quiero cancelar mi pedido",
    "cPaymentNotAccepted": "El pago no fue aceptado",
    "cVatRefund": "Reembolso del IVA",
    "cNotReceived": "No recibí mi pedido",
    "cDelayed": "Mi entrega está retrasada",
    "cCancelOrder": "Quiero cancelar mi pedido",
    "cNotHome": "No estaba en casa durante la entrega, cómo recoger",
    "cWhere": "¿Cómo sé dónde está mi pedido?",
    "cDefect": "Mi producto llegó con defectos",
    "cWrongSpec": "Compré la especificación incorrecta",
    "cNotExpected": "No es lo que esperaba y quiero devolverlo",
    "cAppointment": "Cita con instaladores",
    "cProblems": "Problemas de instalación"
  },
  "fr": {
    "formTitle": "Formulaire de contact",
    "name": "Nom",
    "name_placeholder": "Entrez votre nom",
    "phone_number": "Numéro de téléphone",
    "language": "Langue",
    "email_placeholder": "Entrez votre adresse e-mail",
    "message": "Décrivez brièvement votre question",
    "subject": "Sujet",
    "subject_placeholder": "Veuillez sélectionner un sujet",
    "submit": "Envoyer",
    "submit-success": "La demande de contact a été enregistrée !",
    "submit-success-description": "L'équipe d'assistance clientèle miio prendra contact avec vous dans les plus brefs délais.",
    "error": "Une erreur s'est produite lors de la demande.",
    "select": "Sélectionner l'option",
    "question": "Question",
    "phoneIndicative": "+33",
    "contactReason": "Motif de contact",
    "purchase": "Achat",
    "delivery": "Livraison",
    "returns": "Retours/Échanges",
    "installation": "Installation",
    "contactReasonDetail": "Détail du motif",
    "cPayment": "Impossible de payer",
    "cPrice": "Le prix du produit était différent de l'annonce",
    "cCoupon": "Impossible d'appliquer un coupon",
    "cCancel": "Je veux annuler ma commande",
    "cPaymentNotAccepted": "Le paiement n'a pas été accepté",
    "cVatRefund": "Remboursement de la TVA",
    "cNotReceived": "Je n'ai pas reçu ma commande",
    "cDelayed": "Ma livraison est en retard",
    "cCancelOrder": "Je veux annuler ma commande",
    "cNotHome": "Je n'étais pas chez moi lors de la livraison, comment récupérer",
    "cWhere": "Comment savoir où est ma commande",
    "cDefect": "Mon produit est arrivé défectueux",
    "cWrongSpec": "J'ai acheté la mauvaise spécification",
    "cNotExpected": "Ce n'est pas ce que j'attendais, et je souhaite le retourner",
    "cAppointment": "Rendez-vous avec les installateurs",
    "cProblems": "Problèmes d'installation"
  }
}
</i18n>
